import React from 'react';
import cn from 'classnames';
import FileUploader from 'shared/inputs/FileUploader';
import LoaderSpinner from 'shared/indicators/LoaderSpinner';
// Styles
import './style.scss';
import { theme } from 'src/theme';

if (theme.mode === 'dark') {
  import(/* webpackChunkName: "dark" */ './style.scss?dark');
}

const dragAndDropFilesLimit = 5;

/**
 * A drag and drop area component to upload files.
 *
 * @typedef {Omit<React.ComponentProps<FileUploader>, 'Component'>} FileUploaderProps
 *
 * @typedef {Object} DragAndDropUploaderProps
 * @property {string} [message] A message to display on the drag and drop area.
 * @property {boolean} [isPhoto={false}] Indicates if it is an image uploader. Shows a photo icon if true. False by default.
 *
 * @param {FileUploaderProps & DragAndDropUploaderProps} props Component props. Extends FileUploader props.
 * @returns {React.ReactElement} Drag and drop area component.
 */
const DragAndDropUploader = ({ message, isPhoto = false, filesLimit = dragAndDropFilesLimit, ...props }) => {
  const defaultIconClassName = cn('uploader-icon icon-default k-icon', {
    'k-i-photo': isPhoto,
    'k-i-file': !isPhoto,
  });

  const Component = ({ isUploading, active, ...props }) => {
    const className = cn('drag-and-drop-uploader', { active, 'no-events': isUploading });
    return (
      <div className={className} {...props}>
        {isUploading && (
          <div
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <LoaderSpinner />
            Uploading...
          </div>
        )}
        {!isUploading && (
          <>
            <div className="icon-container">
              {!isUploading && (
                <>
                  <span className={defaultIconClassName} />
                  <span className="uploader-icon icon-active k-icon k-i-upload" />
                </>
              )}
            </div>
            <div>
              {message ?? `Click or drag and drop to upload file${filesLimit > 1 ? 's' : ''}`}
            </div>
          </>
        )}
      </div>
    );
  };

  return <FileUploader Component={Component} filesLimit={filesLimit} {...props} />;
};

export default DragAndDropUploader;
